import { Link, useLocation } from "react-router-dom";

/**
 * Admin header — displayed on every /concierge/* page.
 *
 * The nav links reflect what the signed-in admin can reach:
 *   • Dashboard + Invoices — available to owners and editors alike
 *   • Access                — owner-only; hidden entirely from editors so
 *                             they don't see (and get 403'd on) a link
 *                             they cannot use.
 * Pass `role` in from the calling page so the header can render accordingly.
 */
export default function AdminHeader({ email, role = "editor", onSignOut }) {
    const location = useLocation();
    const isInvoices = location.pathname.startsWith("/concierge/invoices");
    const isProposals = location.pathname.startsWith("/concierge/proposals");
    const isReviews = location.pathname.startsWith("/concierge/reviews");
    const isAccess = location.pathname.startsWith("/concierge/access");
    const isDashboard =
        location.pathname === "/concierge" ||
        location.pathname === "/concierge/";
    return (
        <header
            className="border-b border-[rgba(201,168,106,0.15)] bg-[#0B0B0C]"
            data-testid="admin-header"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 md:gap-6 min-w-0">
                    <a
                        href="/"
                        className="font-serif text-[#C9A86A] text-xl tracking-[0.2em] hover:text-[#D9BC80] transition-colors duration-300 shrink-0"
                        aria-label="Advanced Connected Technologies — return to homepage"
                        data-testid="site-logo-home"
                    >
                        ACT
                    </a>
                    <div className="hidden md:block w-px h-6 bg-[rgba(201,168,106,0.2)]" />
                    <div className="min-w-0">
                        <h1 className="font-serif text-lg md:text-xl text-[#F3EFE7] leading-tight truncate">
                            Concierge
                        </h1>
                        <p className="text-[10px] tracking-[0.32em] uppercase text-[#6e6a62] mt-0.5 truncate">
                            Advanced Connected Technologies
                        </p>
                    </div>
                    <nav
                        className="hidden md:flex items-center gap-6 ml-6 pl-6 border-l border-[rgba(201,168,106,0.15)]"
                        data-testid="admin-nav"
                    >
                        <Link
                            to="/concierge"
                            className={`text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                isDashboard
                                    ? "text-[#C9A86A]"
                                    : "text-[#9A958A] hover:text-[#F3EFE7]"
                            }`}
                            data-testid="admin-nav-dashboard"
                        >
                            Dashboard
                        </Link>
                        <Link
                            to="/concierge/invoices"
                            className={`text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                isInvoices
                                    ? "text-[#C9A86A]"
                                    : "text-[#9A958A] hover:text-[#F3EFE7]"
                            }`}
                            data-testid="admin-nav-invoices"
                        >
                            Invoices
                        </Link>
                        <Link
                            to="/concierge/proposals"
                            className={`text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                isProposals
                                    ? "text-[#C9A86A]"
                                    : "text-[#9A958A] hover:text-[#F3EFE7]"
                            }`}
                            data-testid="admin-nav-proposals"
                        >
                            Proposals
                        </Link>
                        <Link
                            to="/concierge/reviews"
                            className={`text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                isReviews
                                    ? "text-[#C9A86A]"
                                    : "text-[#9A958A] hover:text-[#F3EFE7]"
                            }`}
                            data-testid="admin-nav-reviews"
                        >
                            Reviews
                        </Link>
                        {role === "owner" && (
                            <Link
                                to="/concierge/access/users"
                                className={`text-[11px] tracking-[0.28em] uppercase transition-colors ${
                                    isAccess
                                        ? "text-[#C9A86A]"
                                        : "text-[#9A958A] hover:text-[#F3EFE7]"
                                }`}
                                data-testid="admin-nav-access"
                            >
                                Access
                            </Link>
                        )}
                    </nav>
                </div>
                <div className="flex items-center gap-4 md:gap-6 shrink-0">
                    <span
                        className="hidden md:inline text-[11px] tracking-[0.22em] uppercase text-[#9A958A] truncate max-w-[260px]"
                        data-testid="admin-signed-in-as"
                    >
                        Signed in as {email}
                        {role === "owner" && (
                            <span className="ml-2 text-[#C9A86A]">· owner</span>
                        )}
                    </span>
                    <button
                        type="button"
                        onClick={onSignOut}
                        className="text-[11px] tracking-[0.22em] uppercase text-[#C9A86A] hover:text-[#D9BC80] transition-colors duration-300"
                        data-testid="admin-signout"
                    >
                        Sign Out
                    </button>
                </div>
            </div>
        </header>
    );
}
